import '../styles/GlobalDesign.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const emptyForm = {
  title: '',
  message: '',
  type: 'info',
  mode: 'all',
  singleResellerId: '',
  selectedIds: [],
};

const messageTypes = [
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Succès' },
  { value: 'warning', label: 'Important' },
  { value: 'error', label: 'Urgent' },
];

function AdminNotification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [resellers, setResellers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  }), []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [resellersRes, messagesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/resellers`, { headers }),
        axios.get(`${API_URL}/admin/reseller-messages`, { headers }),
      ]);
      setResellers(resellersRes.data.resellers || []);
      setMessages(messagesRes.data.messages || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/admin/login');
        return;
      }
      setError(err.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredResellers = resellers.filter(reseller => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [reseller.nom, reseller.email, reseller.slug]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(q));
  });

  const toggleSelected = (resellerId) => {
    setForm(prev => {
      const exists = prev.selectedIds.includes(resellerId);
      return {
        ...prev,
        selectedIds: exists
          ? prev.selectedIds.filter(id => id !== resellerId)
          : [...prev.selectedIds, resellerId],
      };
    });
  };

  const buildPayload = () => {
    if (form.mode === 'all') {
      return {
        title: form.title,
        message: form.message,
        type: form.type,
        audience: 'all',
        reseller_ids: [],
      };
    }

    const ids = form.mode === 'one'
      ? [parseInt(form.singleResellerId, 10)].filter(Number.isInteger)
      : form.selectedIds;

    return {
      title: form.title,
      message: form.message,
      type: form.type,
      audience: 'selected',
      reseller_ids: ids,
    };
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!form.title.trim() || !form.message.trim()) {
      setError('Titre et message requis');
      return;
    }

    if (form.mode === 'one' && !form.singleResellerId) {
      setError('Choisissez un revendeur');
      return;
    }

    if (form.mode === 'selected' && form.selectedIds.length === 0) {
      setError('Sélectionnez au moins un revendeur');
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/admin/reseller-messages`, buildPayload(), { headers });
      setNotice(res.data.message || 'Message envoyé');
      setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l’envoi');
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    setError('');
    setNotice('');
    try {
      await axios.delete(`${API_URL}/admin/reseller-messages/${messageId}`, { headers });
      setMessages(prev => prev.filter(message => message.id !== messageId));
      setNotice('Message supprimé');
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-page">
          <div className="admin-loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page reseller-notification-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">Notification</h1>
          <p className="admin-page-subtitle">Messages visibles dans l’onglet Message du panel revendeur.</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {notice && <div className="alert alert-success">{notice}</div>}

        <div className="reseller-message-layout">
          <div className="admin-card reseller-message-form-card">
            <h2 className="card-title">Envoyer un message</h2>
            <form onSubmit={sendMessage} className="reseller-message-form">
              <div className="form-group">
                <label className="form-label">Titre</label>
                <input
                  className="form-input"
                  type="text"
                  value={form.title}
                  onChange={event => setForm(prev => ({ ...prev, title: event.target.value }))}
                  placeholder="Ex: Maintenance prévue"
                  maxLength={160}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={form.type}
                  onChange={event => setForm(prev => ({ ...prev, type: event.target.value }))}
                >
                  {messageTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group reseller-message-full">
                <label className="form-label">Destinataires</label>
                <div className="message-target-tabs">
                  <button type="button" className={form.mode === 'all' ? 'active' : ''} onClick={() => setForm(prev => ({ ...prev, mode: 'all' }))}>
                    Tous
                  </button>
                  <button type="button" className={form.mode === 'one' ? 'active' : ''} onClick={() => setForm(prev => ({ ...prev, mode: 'one' }))}>
                    Individuel
                  </button>
                  <button type="button" className={form.mode === 'selected' ? 'active' : ''} onClick={() => setForm(prev => ({ ...prev, mode: 'selected' }))}>
                    Sélection
                  </button>
                </div>
              </div>

              {form.mode === 'one' && (
                <div className="form-group reseller-message-full">
                  <label className="form-label">Revendeur</label>
                  <select
                    className="form-select"
                    value={form.singleResellerId}
                    onChange={event => setForm(prev => ({ ...prev, singleResellerId: event.target.value }))}
                  >
                    <option value="">Choisir un revendeur</option>
                    {resellers.map(reseller => (
                      <option key={reseller.id} value={reseller.id}>
                        {reseller.nom} - {reseller.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {form.mode === 'selected' && (
                <div className="form-group reseller-message-full">
                  <label className="form-label">Sélection multiple</label>
                  <input
                    className="form-input"
                    type="text"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Rechercher nom, email ou slug"
                  />
                  <div className="reseller-select-list">
                    {filteredResellers.map(reseller => (
                      <label key={reseller.id} className="reseller-select-row">
                        <input
                          type="checkbox"
                          checked={form.selectedIds.includes(reseller.id)}
                          onChange={() => toggleSelected(reseller.id)}
                        />
                        <span>
                          <strong>{reseller.nom}</strong>
                          <small>{reseller.email} / {reseller.slug}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="reseller-message-count">{form.selectedIds.length} sélectionné(s)</div>
                </div>
              )}

              <div className="form-group reseller-message-full">
                <label className="form-label">Message</label>
                <textarea
                  className="form-input reseller-message-textarea"
                  value={form.message}
                  onChange={event => setForm(prev => ({ ...prev, message: event.target.value }))}
                  placeholder="Votre message..."
                  rows={8}
                />
              </div>

              <div className="reseller-message-preview reseller-message-full">
                <div className={`reseller-message-card type-${form.type}`}>
                  <div className="reseller-message-card-head">
                    <strong>{form.title || 'Titre du message'}</strong>
                    <span>{messageTypes.find(type => type.value === form.type)?.label}</span>
                  </div>
                  <p>{form.message || 'Le message apparaîtra ici.'}</p>
                </div>
              </div>

              <div className="form-actions reseller-message-full">
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  {sending ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>

          <div className="admin-card reseller-message-history-card">
            <h2 className="card-title">Historique</h2>
            {messages.length === 0 ? (
              <div className="empty-state">Aucun message envoyé</div>
            ) : (
              <div className="reseller-message-history">
                {messages.map(message => (
                  <div key={message.id} className={`reseller-message-history-item type-${message.type}`}>
                    <div className="reseller-message-history-head">
                      <div>
                        <strong>{message.title}</strong>
                        <small>{new Date(message.created_at).toLocaleString('fr-FR')}</small>
                      </div>
                      <button type="button" className="rs-btn rs-btn-delete" onClick={() => deleteMessage(message.id)} title="Supprimer">
                        ×
                      </button>
                    </div>
                    <p>{message.message}</p>
                    <div className="reseller-message-meta">
                      <span>{message.audience === 'all' ? 'Tous les revendeurs actifs' : 'Sélection'}</span>
                      <span>{message.read_count || 0}/{message.target_count || 0} lus</span>
                    </div>
                    {message.target_names && (
                      <div className="reseller-message-targets">{message.target_names}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminNotification;
